import type { TestType } from '@/models/SharedTypes';
import type { ExamScheduleEntry, ExamStatus } from '@/models/UserTypes';
import { calculateExamCountdown } from '@/features/dashboard/utils';

/**
 * Pure accessors for the status-first exam schedule. The single place every
 * consumer (dashboard, preparedness, study home) reads a track's schedule from,
 * so display never diverges again between the legacy `examDate` and the map.
 *
 * Date math routes through `calculateExamCountdown` (features/dashboard/utils) —
 * the ONE safe date-only parser (guarded by examDate.timezone.test.ts). Never
 * `new Date(dateString)` here: that reintroduces the UTC off-by-one west of UTC.
 */

// LCSW eligibility goals (state-board rule: 3,000 clinical + 100 supervision hrs).
export const LCSW_CLINICAL_HOURS_GOAL = 3000;
export const LCSW_SUPERVISION_HOURS_GOAL = 100;

/** Minimal shape needed to resolve a schedule — accepts a full or partial profile. */
interface ProfileLike {
  examSchedule?: Partial<Record<string, ExamScheduleEntry>> | null;
  examDates?: Partial<Record<string, { date: string; setAt: string }>> | null;
  examDate?: string | null;
  /** Server-reconciled soonest scheduled exam (epoch ms). Preferred over the
   *  legacy client-side date fields when present (dumb client). */
  nextScheduledExamMs?: number | null;
  targetExam?: TestType | string | null;
}

/**
 * The single accessor for a track's schedule entry. Resolution order:
 *   1. the explicit `examSchedule` map (richest — status/window/hours),
 *   2. the server-reconciled `nextScheduledExamMs` scalar for the target track
 *      (dumb client: the backend already reconciled examDate/examDates/examSchedule),
 *   3. the legacy `examDates`/`examDate` string fields (client date resolution),
 *      kept only as a fallback for profiles fetched before the scalar existed.
 */
export function entryFor(
  profile: ProfileLike | null | undefined,
  testType: TestType
): ExamScheduleEntry | null {
  const entry = profile?.examSchedule?.[testType];
  if (entry) return entry;

  // Prefer the server scalar for the target track. Extract the UTC calendar date
  // ('YYYY-MM-DD') so calculateExamCountdown parses it at LOCAL midnight — the
  // same tz-safe handling the legacy UTC-midnight examDate ISO gets downstream.
  if (
    profile?.targetExam === testType &&
    typeof profile?.nextScheduledExamMs === 'number'
  ) {
    const isoDate = new Date(profile.nextScheduledExamMs).toISOString().split('T')[0];
    return { status: 'scheduled', date: isoDate, updatedAt: '' };
  }

  const legacyDate =
    profile?.examDates?.[testType]?.date ??
    (profile?.targetExam === testType ? profile?.examDate ?? null : null);

  if (legacyDate) return { status: 'scheduled', date: legacyDate, updatedAt: '' };
  return null;
}

/**
 * Days until the exam — ONLY meaningful for a `scheduled` entry with a date.
 * Returns null for every other status (there is no hard date to count down to)
 * and for a past date (a stale/expired schedule shows no negative countdown).
 */
export function daysUntilFor(entry: ExamScheduleEntry | null | undefined): number | null {
  if (!entry || entry.status !== 'scheduled' || !entry.date) return null;
  const info = calculateExamCountdown(entry.date);
  if (info.isPast) return null;
  return info.daysRemaining;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** '2026-11' → 'November 2026'; '2026-Q4' → 'Q4 2026'; else passthrough. */
export function formatWindow(window: string): string {
  const quarter = window.match(/^(\d{4})-Q([1-4])$/);
  if (quarter) return `Q${quarter[2]} ${quarter[1]}`;
  const month = window.match(/^(\d{4})-(\d{2})$/);
  if (month) {
    const idx = Number(month[2]) - 1;
    if (idx >= 0 && idx < 12) return `${MONTH_NAMES[idx]} ${month[1]}`;
  }
  return window;
}

export type ExamScheduleDisplayKind = 'countdown' | 'window' | 'hours' | 'prompt';

/** What a consumer renders for a track — a discriminated view of the entry. */
export interface ExamScheduleDisplay {
  kind: ExamScheduleDisplayKind;
  text: string;            // one-line headline the UI can show as-is
  status?: ExamStatus;
  days?: number;           // countdown
  window?: string;         // window (raw, e.g. '2026-11')
  clinical?: number;       // accruing
  supervision?: number;    // accruing
  clinicalGoal?: number;   // accruing
  supervisionGoal?: number;// accruing
}

/**
 * Maps an entry to its display. The whole point of the redesign: EVERY status
 * yields something to render — never a dead "set a date" wall. Missing,
 * exploring, and stale-scheduled all resolve to the gentle `prompt`.
 */
export function displayFor(entry: ExamScheduleEntry | null | undefined): ExamScheduleDisplay {
  if (!entry) return { kind: 'prompt', text: 'Set your exam schedule' };

  switch (entry.status) {
    case 'scheduled': {
      const days = daysUntilFor(entry);
      if (days === null) return { kind: 'prompt', text: 'Set your exam schedule', status: 'scheduled' };
      return {
        kind: 'countdown',
        status: 'scheduled',
        days,
        text: days === 0 ? 'Exam day is here' : `${days} ${days === 1 ? 'day' : 'days'} to go`,
      };
    }
    case 'approved':
    case 'waiting': {
      const w = entry.targetWindow ? formatWindow(entry.targetWindow) : null;
      let text: string;
      if (entry.status === 'approved') {
        text = w ? `Aiming for ${w}: book your seat` : 'Approved: book your seat';
      } else {
        text = w ? `Aiming for ${w}` : 'Waiting on board approval';
      }
      return { kind: 'window', status: entry.status, window: entry.targetWindow, text };
    }
    case 'accruing': {
      const clinical = entry.hours?.clinical ?? 0;
      const supervision = entry.hours?.supervision ?? 0;
      return {
        kind: 'hours',
        status: 'accruing',
        clinical,
        supervision,
        clinicalGoal: LCSW_CLINICAL_HOURS_GOAL,
        supervisionGoal: LCSW_SUPERVISION_HOURS_GOAL,
        text: `${clinical}/${LCSW_CLINICAL_HOURS_GOAL} clinical hrs`,
      };
    }
    case 'exploring':
    default:
      return { kind: 'prompt', status: entry.status, text: 'Just exploring: build your base' };
  }
}
